import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Code2 } from 'lucide-react';
import { usePageSeo } from '../hooks/usePageSeo';
import { absoluteUrl } from '../utils/seo';
import { supabase } from '../lib/supabase';
interface Props {
  darkMode: boolean;
}

const EmbedDocs: React.FC<Props> = ({ darkMode }) => {
  const [searchParams] = useSearchParams();
  const presetForumId = searchParams.get('forum_id')?.trim() || '';
  usePageSeo({
    title: 'Forum istatistik embed ve API',
    description: 'Sitenize forum istatistik widgetı ve JSON API entegrasyonu ekleyin.',
    keywords: 'forum embed, widget, api, iframe',
    canonical: absoluteUrl('/embed-docs'),
  });

  const [forums, setForums] = useState<{ forum_id: string; name: string; members_count: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedForumId, setSelectedForumId] = useState('');
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');
  const [compact, setCompact] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('forums')
        .select('forum_id, name, members_count')
        .eq('is_active', true)
        .order('members_count', { ascending: false })
        .limit(500);
      setForums((data || []) as any);
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    if (presetForumId) {
      setSelectedForumId(presetForumId);
    }
  }, [presetForumId]);

  const hasForum = selectedForumId.length > 0;

  const selectedLabel = useMemo(() => {
    if (!hasForum) return 'Bir forum seçiniz';
    const f = forums.find((x) => x.forum_id === selectedForumId);
    return f ? `${f.name} (${f.forum_id})` : selectedForumId;
  }, [forums, selectedForumId, hasForum]);

  const embedUrl = useMemo(() => {
    if (!hasForum) return '';
    const base = `https://kitapcik.net/embed/${encodeURIComponent(selectedForumId)}`;
    const params = new URLSearchParams();
    params.set('theme', theme);
    params.set('accent', '#6366f1');
    if (compact) params.set('compact', '1');
    return `${base}?${params.toString()}`;
  }, [selectedForumId, theme, compact, hasForum]);

  const iframeHeight = compact ? 300 : 280;

  const example = useMemo(() => {
    if (!embedUrl) {
      return '<!-- Bir forum seçiniz -->';
    }
    return `<iframe src="${embedUrl}" width="${compact ? 360 : 420}" height="${iframeHeight}" frameborder="0" scrolling="no" title="Forum istatistikleri"></iframe>`;
  }, [embedUrl, compact, iframeHeight]);

  const apiUrl = useMemo(() => {
    if (!hasForum) return '';
    return `https://kitapcik.net/forum-stats-api.php?forum_id=${encodeURIComponent(selectedForumId)}`;
  }, [selectedForumId, hasForum]);

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50'}`}>
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Link to="/" className={`inline-flex items-center gap-2 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          <ArrowLeft size={20} /> Geri
        </Link>
        <h1 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <Code2 /> Embed & API
        </h1>
        <section className={`rounded-xl p-6 mb-6 ${darkMode ? 'bg-gray-800' : 'bg-white shadow'}`}>
          <h2 className="font-semibold mb-2">iframe widget</h2>
          <div className="grid gap-3 sm:grid-cols-2 mb-4">
            <div className="sm:col-span-2">
              <label className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                Forum seç
              </label>
              <select
                value={selectedForumId}
                onChange={(e) => setSelectedForumId(e.target.value)}
                disabled={loading}
                className={`mt-1 w-full px-3 py-2 rounded-lg border text-sm ${
                  darkMode
                    ? 'bg-gray-900 border-gray-700 text-gray-100'
                    : 'bg-white border-gray-200 text-gray-900'
                }`}
              >
                <option value="">{loading ? 'Yükleniyor…' : 'Bir forum seçiniz'}</option>
                {forums.map((f) => (
                  <option key={f.forum_id} value={f.forum_id}>
                    {f.name} ({f.forum_id})
                  </option>
                ))}
              </select>
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Seçili: <span className={darkMode ? 'text-indigo-300' : 'text-indigo-700'}>{selectedLabel}</span>
              </p>
            </div>
            <div>
              <label className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                Tema
              </label>
              <select
                value={theme}
                onChange={(e) => setTheme(e.target.value === 'light' ? 'light' : 'dark')}
                className={`mt-1 w-full px-3 py-2 rounded-lg border text-sm ${
                  darkMode
                    ? 'bg-gray-900 border-gray-700 text-gray-100'
                    : 'bg-white border-gray-200 text-gray-900'
                }`}
              >
                <option value="dark">dark</option>
                <option value="light">light</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className={`inline-flex items-center gap-2 text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                <input
                  type="checkbox"
                  checked={compact}
                  onChange={(e) => setCompact(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                />
                Compact
              </label>
            </div>
          </div>
          <pre className={`text-xs p-4 rounded-lg overflow-x-auto ${darkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
            {example}
          </pre>
          <div className="mt-4">
            <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Önizleme</p>
            <div className={`rounded-xl overflow-hidden border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              {embedUrl ? (
                <iframe
                  src={embedUrl}
                  width="100%"
                  height={iframeHeight}
                  scrolling="no"
                  title="Forum istatistikleri önizleme"
                  style={{ border: 0, display: 'block' }}
                />
              ) : (
                <div
                  className={`flex items-center justify-center text-sm ${
                    darkMode ? 'bg-gray-900 text-gray-500' : 'bg-gray-100 text-gray-500'
                  }`}
                  style={{ height: iframeHeight }}
                >
                  Bir forum seçiniz — önizleme burada görünür
                </div>
              )}
            </div>
          </div>
        </section>
        <section className={`rounded-xl p-6 ${darkMode ? 'bg-gray-800' : 'bg-white shadow'}`}>
          <h2 className="font-semibold mb-2">JSON API (salt okunur)</h2>
          <code className={`text-sm break-all ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
            {apiUrl || 'Bir forum seçiniz — API adresi burada görünür.'}
          </code>
          <p className={`text-sm mt-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            GET isteği ile forum_id slug’ına göre güncel sayıları JSON döner.
          </p>
        </section>
      </div>
    </div>
  );
};

export default EmbedDocs;
